import React, { useState, useContext } from 'react';
import { X, Phone, MessageCircle, Video, Users, Coffee, FileText } from 'lucide-react';
import { LanguageContext } from '../../../../Context';



/**
 * ContactLogDialog - Modal dialog for recording a new customer contact.
 * Captures the contact method, notes, and outcome with bilingual labels.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is visible
 * @param {Function} props.onClose - Callback to close the dialog
 * @param {Function} props.onSave - Callback with contact data to save
 * @param {string} props.leadName - Name or phone of the lead being contacted
 * @returns {JSX.Element}
 * @author Forge, opencode glm-5.1
 * @version 1.1
 * @since 2026-04-15
 */

const CONTACT_METHODS = [
  { id: 'call', labelKey: 'pipeline.phoneCall', fallback: 'Phone Call', icon: Phone, color: '#3b82f6' },
  { id: 'zalo', labelKey: 'pipeline.zalo', fallback: 'Zalo', icon: MessageCircle, color: '#06b6d4' },
  { id: 'visit', labelKey: 'pipeline.visit', fallback: 'Visit', icon: Coffee, color: '#f59e0b' },
  { id: 'video', labelKey: 'pipeline.videoCall', fallback: 'Video Call', icon: Video, color: '#8b5cf6' },
  { id: 'meeting', labelKey: 'pipeline.meeting', fallback: 'Meeting', icon: Users, color: '#10b981' },
  { id: 'other', labelKey: 'pipeline.other', fallback: 'Other', icon: FileText, color: '#64748b' },
];

const OUTCOMES = [
  { id: 'noAnswer', labelKey: 'pipeline.noAnswer', fallback: 'No answer' },
  { id: 'leftMessage', labelKey: 'pipeline.leftMessage', fallback: 'Left message' },
  { id: 'interested', labelKey: 'pipeline.interested', fallback: 'Interested' },
  { id: 'notInterested', labelKey: 'pipeline.notInterested', fallback: 'Not interested' },
  { id: 'callBackLater', labelKey: 'pipeline.callBackLater', fallback: 'Call back later' },
  { id: 'bookedTest', labelKey: 'pipeline.bookedTest', fallback: 'Booked test' },
  { id: 'followUp', labelKey: 'pipeline.followUp', fallback: 'Follow up needed' },
  { id: 'completed', labelKey: 'pipeline.completed', fallback: 'Completed' },
];

export default function ContactLogDialog({ open, onClose, onSave, leadName }) {
  const { l, language } = useContext(LanguageContext);
  

  const t = (key, fallback) => {
    try { return l[key]?.[language] || fallback || key; } catch { return fallback || key; }
  };

  const [method, setMethod] = useState('call');
  const [notes, setNotes] = useState('');
  const [outcome, setOutcome] = useState('');

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!notes.trim() && !outcome) return;
    onSave({ method, notes, outcome });
    setMethod('call');
    setNotes('');
    setOutcome('');
    onClose();
  };

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const inputClass = `w-full px-3 py-2 rounded-[10px] border border-slate-200 text-sm text-slate-800 placeholder:text-slate-300 focus:outline-none focus:border-teal-800 focus:ring-1 focus:ring-teal-800/20 transition-colors duration-150`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(2px)' }}
      onClick={handleBackdrop}
    >
      <div className="bg-white rounded-[12px] shadow-[0_8px_30px_rgba(0,0,0,0.12)] w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-bold text-slate-800">{t('pipeline.logContact', 'Log Contact')}</h2>
            <p className="text-xs text-slate-500 mt-0.5">{leadName}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors duration-150 cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-4">
            {/* Method */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                {t('pipeline.method', 'Method')}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {CONTACT_METHODS.map(m => {
                  const Icon = m.icon;
                  const isSelected = method === m.id;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setMethod(m.id)}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-[10px] border text-xs font-medium transition-all duration-150 cursor-pointer ${
                        isSelected
                          ? 'border-2 shadow-sm'
                          : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                      style={isSelected ? {
                        borderColor: m.color,
                        backgroundColor: `${m.color}10`,
                        color: m.color,
                      } : {}}
                    >
                      <Icon size={18} />
                      <span>{t(m.labelKey, m.fallback)}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Outcome */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                {t('pipeline.outcome', 'Outcome')}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {OUTCOMES.map(o => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setOutcome(outcome === o.id ? '' : o.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-150 border cursor-pointer ${
                      outcome === o.id
                        ? 'bg-slate-800 text-white border-slate-800'
                        : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {t(o.labelKey, o.fallback)}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                {t('pipeline.notes', 'Notes')}
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className={`${inputClass} resize-none`}
                placeholder={t('pipeline.whatWasDiscussed', 'What was discussed...')}
                autoFocus
              />
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-[10px] text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors duration-150 cursor-pointer"
            >
              {t('pipeline.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="px-5 py-2 rounded-[10px] text-sm font-semibold text-white bg-teal-800 hover:bg-teal-900 transition-colors duration-150 cursor-pointer"
            >
              {t('pipeline.saveContact', 'Save Contact')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
